import { useEffect, useState, useRef } from 'react'
import confetti from 'canvas-confetti'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'

// GRK-BRAND-6: celebration confetti in the Grackle palette.
const CELEBRATION_COLORS = [
  GRACKLE_COLORS.pollen,
  GRACKLE_COLORS.pistachio,
  GRACKLE_COLORS.powderBlue,
  GRACKLE_COLORS.navy,
  CATEGORICAL.raspberry.solid,
]

interface ClosedWonCelebrationProps {
  opportunityName: string
  amount: number
  onComplete: () => void
}

/**
 * Full-screen celebration overlay with fireworks and thumbs up image.
 * Displays for 3 seconds when an opportunity is changed to "Closed Won".
 */
export function ClosedWonCelebration({
  opportunityName,
  amount,
  onComplete,
}: ClosedWonCelebrationProps) {
  const [visible, setVisible] = useState(true)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Create confetti instance bound to our canvas (renders inside overlay, not at body level)
    const myConfetti = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: false, // Disabled due to CSP blocking blob: workers in production
    })

    // Fire confetti/fireworks for 3 seconds
    const duration = 3000
    const end = Date.now() + duration

    if (!prefersReducedMotion) {
      const frame = () => {
        // Left side fireworks
        myConfetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: CELEBRATION_COLORS,
        })
        // Right side fireworks
        myConfetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: CELEBRATION_COLORS,
        })

        if (Date.now() < end) {
          requestAnimationFrame(frame)
        }
      }
      frame()
    }

    // Auto-dismiss after 3 seconds
    const timer = setTimeout(() => {
      setVisible(false)
      onComplete()
    }, duration)

    return () => {
      clearTimeout(timer)
      myConfetti.reset() // Clean up confetti particles
    }
  }, [onComplete])

  if (!visible) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50"
      role="dialog"
      aria-label="Celebration for closed won opportunity"
      aria-modal="true"
    >
      {/* Confetti canvas - renders inside overlay so it appears above dark background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      />
      <div className="text-center">
        <img
          src="/images/GrackleThumbsUp.png"
          alt="Grackle Thumbs Up - Congratulations!"
          className="w-64 h-64 mx-auto drop-shadow-2xl animate-bounce"
        />
        <h2 className="mt-4 text-5xl font-bold text-white drop-shadow-lg">Deal Won!</h2>
        <p className="mt-3 text-3xl text-white/90">{opportunityName}</p>
        {amount > 0 && (
          <p className="mt-1 text-2xl text-[#FFC857] font-semibold">${amount.toLocaleString()}</p>
        )}
      </div>
    </div>
  )
}
